/** Every canvas node maps to UniversalNode via this RF ``node.type``; real template id is ``data.nodeType``. */
export const CURIO_UNIVERSAL_NODE_TYPE = "__curioUniversalNode" as const;

/** The name of a dataflow nobody has named yet: a fresh canvas, and File > New. */
export const DEFAULT_WORKFLOW_NAME = "DefaultDataflow";

/**
 * Canonical unversioned node-type identifiers.
 *
 * Each value is the canonical package-template id (`<packageId>/<templateId>`) that
 * resolves through the registry's unversioned-latest-major lookup. The
 * enum keys keep the legacy names so existing dispatch code in
 * `NotebookConvertor` / `useNodeState` etc. keeps compiling unchanged —
 * but at runtime they emit canonical strings.
 *
 * IMPORTANT: every value here is UNVERSIONED. Nodes dragged from the
 * palette carry the versioned canonical id (`curio.builtin/data-pool@1`,
 * see `packagesClient.buildDescriptor`) and that form is what gets
 * persisted into the trill spec. There is no load-time normalizer, so any
 * comparison against these values must first strip the `@<major>` suffix
 * via `unversionedNodeType` / `getUnversionedFlowNodeType`
 * (`utils/flowNodeCanonicalType.ts`). Comparing a raw `data.nodeType`
 * against one of these is a silent no-match — that was #169 and #159.
 */
export enum NodeType {
  DATA_LOADING = "curio.builtin/data-loading",
  DATA_EXPORT = "curio.builtin/data-export",
  DATA_TRANSFORMATION = "curio.builtin/data-transformation",
  COMPUTATION_ANALYSIS = "curio.builtin/computation-analysis",
  DATA_SUMMARY = "curio.builtin/data-summary",
  VIS_VEGA = "curio.builtin/vis-vega",
  VIS_SIMPLE = "curio.builtin/vis-simple",
  DATA_POOL = "curio.builtin/data-pool",
  JS_COMPUTATION = "curio.builtin/js-computation",
  AUTK_GRAMMAR = "curio.builtin/autk-grammar",
  EDIT_FEATURES = "curio.builtin/edit-features",
}

export enum EdgeType {
  BIDIRECTIONAL_EDGE = "BIDIRECTIONAL_EDGE",
  UNIDIRECTIONAL_EDGE = "UNIDIRECTIONAL_EDGE"
}

export enum SupportedType {
  DATAFRAME = "DATAFRAME",
  GEODATAFRAME = "GEODATAFRAME",
  VALUE = "VALUE", // int, float, boolean, ...
  LIST = "LIST",
  JSON = "JSON", // dictionary in python
  RASTER = "RASTER",
}

export enum VisInteractionType {
  POINT = "POINT",
  INTERVAL = "INTERVAL",
  UNDETERMINED = "UNDETERMINED",
}

// resolution between plots
export enum ResolutionType {
  OVERWRITE = "OVERWRITE", // last interacted with overwrites other interactions
  MERGE_AND = "MERGE_AND", // all plots need to interact
  MERGE_OR = "MERGE_OR", // at least one plot needs to interact
}

export enum AccessLevelType {
  PROGRAMMER = "PROGRAMMER", // only programmers can access
  EXPERT = "EXPERT", // only experts can access
  ANY = "ANY", // both can access
}

// Node box geometry. The renderer (components/styles.tsx NodeContainer) is the
// authority on these; they live here so layout utilities can share them without
// importing a component and closing a cycle back through the provider stack.
export const DEFAULT_NODE_WIDTH = 525;
export const DEFAULT_NODE_HEIGHT = 350;
export const MIN_NODE_WIDTH = 200;
export const MIN_NODE_HEIGHT = 150;
// A minimized node's chip: its icon and its title, cut with an ellipsis. The
// width is fixed, not sized to the title, so `utils/nodeBoxSize.ts` can mirror
// it without measuring text.
export const MINIMIZED_NODE_WIDTH = 250;
export const MINIMIZED_NODE_HEIGHT = 40;
